/*
 * How to Add a Step:
 *
 * 1.  Add something like `BLANK: new FitStep({}),` to FIT_STEPS`.
 * 2.  Add an `id` that's unique.
 * 3.  Add the component that renders the step to ./getStepComponent.js
 * 4.  Add an `slug` that's unique among the steps being used in your flow.
 * 5.  Add an `seoDescription` and `seoTitle`.
 * 6.  Add any flags. For example, IS_SURVEY indicates that this step is part of the survey.
 * 7.  Add a `gate` function. The gate prevents users from visiting this step unless it returns true. If false,
 *     the user will be redirected to the previous step and so on and so forth.
 * 8.  Give the step a `title` (or not). This title can be used for the title of the step on its own page and
 *      on the Summary page.
 * 9.  If your step stores data, create an entry in valid.js and add it like `validate: valid.BLANK`.
 *      This will be used to validate input.
 * 10. If your step stores data, give it a `type` that extends SurveyValue. The type will be used for
 *      transforming the step to and from a string.
 * 11. Add the step to the DEFAULT_FIT_FLOW!
 */

import * as valid from './valid'

import * as fit from '../../store/fit'
import * as sizes from '../../store/sizes'

import FitStep from '../../types/FitStep'
import Height from '../../types/Height'
import JacketLength from '../../types/JacketLength'
import Preference from '../../types/Preference'
import ShoulderShape from '../../types/ShoulderShape'
import StomachShape from '../../types/StomachShape'
import SurveyNumber from '../../types/SurveyNumber'
import Weight from '../../types/Weight'
import * as URLS from '../../urls'

export const gateSurveyStep = (state, step) =>
  fit.hasSurveyResponsesUpTo(state, step) ? false : fit.furthestAcceptableSurveyStepUrl(state)

/**
 * If a user has sizes, return false so they can go to their summary.
 * However, if a user does not have sizes, route them to a different page depending on if they have
 * progressed through the Fit Survey already or not. If oldStepSlug is defined, they've started the
 * survey, so take them back to their next survey step. Otherwise, direct them to the fit surevy
 * landing page. This will not persist beyond a reload, as state is cleared on page reload.
 *
 * @param {Object} [state] application state
 * @return {boolean|string}
 */
export const gateSizes = (state) => {
  if (sizes.exist(state)) {
    return false
  }

  if (fit.oldStepSlug(state)) {
    return fit.furthestAcceptableSurveyStepUrl(state)
  }

  return URLS.FIT_INTRO_SLUG
}

export const FIT_STEPS = {
  INTRO: new FitStep({
    id: 'intro',
    seoDescription:
      'Spend a few minutes filling out our Fit Survey, and we’ll plug your information into our ' +
      'Fit Algorithm to ensure you get the sizes you need.',
    seoTitle: 'Find Your Fit',
    slug: URLS.FIT_INTRO_SLUG,
    title: 'The Fit Survey',
  }),
  BIRTH_YEAR: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'birthYear',
    seoDescription: 'Enter your birth year.',
    seoTitle: 'Birth Year',
    slug: 'birth-year',
    title: 'What year were you born?',
    type: SurveyNumber,
    validate: valid.BIRTH_YEAR,
  }),
  CHEST: new FitStep({
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'chest',
    seoDescription: 'Select your suit jacket size.',
    seoTitle: 'Jacket Size',
    slug: 'jacket-size',
    title: 'What is your suit jacket size?',
    type: SurveyNumber,
    validate: valid.CHEST,
  }),
  HEIGHT: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'height',
    seoDescription: 'Select your height.',
    seoTitle: 'Height',
    slug: 'height',
    title: 'How tall are you?',
    type: Height,
    validate: valid.HEIGHT,
  }),
  INSEAM: new FitStep({
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'inseam',
    seoDescription: 'Select your pant length (inseam).',
    seoTitle: 'Inseam',
    slug: 'pant-inseam',
    title: 'What is your pant length (inseam)?',
    type: SurveyNumber,
    validate: valid.INSEAM,
  }),
  JACKET_LENGTH: new FitStep({
    validate: valid.PASS,
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'jacketLength',
    seoDescription: 'Select your jacket length.',
    seoTitle: 'Jacket Length',
    slug: 'jacket-length',
    title: 'What is your suit jacket length?',
    type: JacketLength,
  }),
  NECK: new FitStep({
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'neck',
    seoDescription: 'Select your dress shirt neck size.',
    seoTitle: 'Neck',
    slug: 'shirt-neck',
    title: 'What is your dress shirt neck size?',
    type: SurveyNumber,
    validate: valid.NECK,
  }),
  PREFERENCE: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'preferredFit',
    seoDescription: 'Select your fit preference.',
    seoTitle: 'Preference',
    slug: 'preference',
    title: 'How do you like your suit to fit?',
    type: Preference,
    validate: valid.PASS,
  }),
  SHOE: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'shoe',
    seoDescription: 'Select your shoe size.',
    seoTitle: 'Shoe',
    slug: 'shoe',
    title: 'What is your shoe size?',
    type: SurveyNumber,
    validate: valid.SHOE,
  }),
  RECCO: new FitStep({
    gate: gateSizes,
    title: 'My Sizes',
    id: 'recco',
    seoDescription: 'Review and confirm your sizes.',
    seoTitle: 'My Sizes',
    slug: URLS.FIT_SIZES_SLUG,
  }),
  SLEEVE: new FitStep({
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'sleeve',
    seoDescription: 'Select your sleeve length.',
    seoTitle: 'Sleeve',
    slug: 'shirt-sleeve',
    title: 'What is your dress shirt sleeve length?',
    type: SurveyNumber,
    validate: valid.SLEEVE,
  }),
  SHOULDER_SHAPE: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'shoulderShape',
    seoDescription: 'Select the shape which matches your shoulders.',
    seoTitle: 'Shoulders',
    slug: 'shoulders',
    title: 'What best describes your shoulders?',
    type: ShoulderShape,
    validate: valid.PASS,
  }),
  STOMACH_SHAPE: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'stomachShape',
    seoDescription: 'Select the shape which matches your stomach.',
    seoTitle: 'Stomach',
    slug: 'stomach',
    title: 'What best describes your stomach?',
    type: StomachShape,
    validate: valid.PASS,
  }),
  SURVEY_SUMMARY: new FitStep({
    flags: {
      SKIP: true,
    },
    gate: gateSizes,
    id: 'summary',
    seoDescription: 'Here are the answers from your Fit Survey.',
    seoTitle: 'Survey Answers',
    slug: URLS.FIT_SUMMARY_SLUG,
    title: 'Your Fit Survey Answers',
  }),
  WAIST: new FitStep({
    flags: {
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'waist',
    seoDescription: 'Select your waist size.',
    seoTitle: 'Waist',
    slug: 'pant-waist',
    title: 'What is your waist size?',
    type: SurveyNumber,
    validate: valid.WAIST,
  }),
  WEIGHT: new FitStep({
    flags: {
      IS_REQUIRED: true,
      IS_SURVEY: true,
    },
    gate: gateSurveyStep,
    id: 'weight',
    seoDescription: 'Enter your weight.',
    seoTitle: 'Weight',
    slug: 'weight',
    title: 'What is your weight?',
    type: Weight,
    validate: valid.WEIGHT,
  }),
}

const FIT_FLOW_V1 = [
  FIT_STEPS.INTRO,
  FIT_STEPS.HEIGHT,
  FIT_STEPS.WEIGHT,
  FIT_STEPS.BIRTH_YEAR,
  FIT_STEPS.SHOULDER_SHAPE,
  FIT_STEPS.STOMACH_SHAPE,
  FIT_STEPS.SHOE,
  FIT_STEPS.WAIST,
  FIT_STEPS.INSEAM,
  FIT_STEPS.NECK,
  FIT_STEPS.SLEEVE,
  FIT_STEPS.CHEST,
  FIT_STEPS.JACKET_LENGTH,
  FIT_STEPS.PREFERENCE,
  FIT_STEPS.SURVEY_SUMMARY,
  FIT_STEPS.RECCO,
]

export const DEFAULT_FIT_FLOW = FIT_FLOW_V1
